import 'regenerator-runtime';
import '@platform/elements/esm/index.css';
import '@interhyp/platform-navigation/esm/index.css';
import { apm } from '@elastic/apm-rum';
import { ApmRoute } from '@elastic/apm-rum-react';
import { OPEN_SUBMISSION_DOCUMENTS_OVERVIEW, OPEN_SUBMISSION_OVERVIEW } from '@interhyp/api-credit';
import { OPEN_LOAN_PARTNER_COMMUNICATION_OVERVIEW } from '@interhyp/api-credit-communication';
import {
  OPEN_EXISTING_DATA_INQUIRY_DOCUMENTS_OVERVIEW,
  OPEN_EXISTING_DATA_INQUIRY_OVERVIEW,
} from '@interhyp/api-existing-data-inquiry';
import { OPEN_INQUIRY_DOCUMENTS_OVERVIEW, OPEN_INQUIRY_OVERVIEW } from '@interhyp/api-preinquiry';
import { Footer } from '@interhyp/platform-navigation';
import { Toaster } from '@platform/elements';
import Axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Redirect, Switch, useHistory } from 'react-router-dom';

import styles from '../../../Application.module.scss';
import { COMMUNICATION_PLUGIN_ENABLED } from '../../../constants/env';
import { useFrontendNotifications } from '../../../hooks/useFrontendNotifications';
import { ArchiveWrapper } from '../../viewWrappers/archive/ArchiveWrapper';
import { DashboardWrapper } from '../../viewWrappers/dashboard/DashboardWrapper';
import { ExistingDataInquiryDocumentsWrapper } from '../../viewWrappers/existingDataInquiry/ExistingDataInquiryDocumentsWrapper';
import { ExistingDataInquiryOverviewWrapper } from '../../viewWrappers/existingDataInquiry/ExistingDataInquiryOverviewWrapper';
import { InquiryCommunicationOverviewWrapper } from '../../viewWrappers/inquiries/InquiryCommunicationOverviewWrapper';
import { InquiryDocumentsWrapper } from '../../viewWrappers/inquiries/InquiryDocumentsWrapper';
import { InquiryOverviewWrapper } from '../../viewWrappers/inquiries/InquiryOverviewWrapper';
import { SubmissionCommunicationOverviewWrapper } from '../../viewWrappers/submission/SubmissionCommunicationOverviewWrapper';
import { SubmissionDocumentsWrapper } from '../../viewWrappers/submission/SubmissionDocumentsWrapper';
import { SubmissionOverviewWrapper } from '../../viewWrappers/submission/SubmissionOverviewWrapper';

export interface AuthenticatedAppProps {}

export default function AuthenticatedApp({}: AuthenticatedAppProps): JSX.Element {
  const [currentUserId, setCurrentUserId] = useState<string>(null);
  const [providerId, setProviderId] = useState<string>(null);
  const { push } = useHistory();

  OPEN_INQUIRY_OVERVIEW.useEvent(event =>
    window.open(`/credit/inquiries/${event.payload.inquiryId}`, '_blank').focus(),
  );
  OPEN_INQUIRY_DOCUMENTS_OVERVIEW.useEvent(event =>
    push(`/credit/inquiries/${event.payload.inquiryId}/documents`),
  );
  OPEN_SUBMISSION_OVERVIEW.useEvent(event =>
    window.open(`/credit/submissions/${event.payload.submissionId}`, '_blank').focus(),
  );
  OPEN_SUBMISSION_DOCUMENTS_OVERVIEW.useEvent(event =>
    push(`/credit/submissions/${event.payload.submissionId}/documents`),
  );

  OPEN_LOAN_PARTNER_COMMUNICATION_OVERVIEW.useEvent(event => {
    if (event.payload.resourceType === 'PRE_INQUIRY') {
      push(`/credit/inquiries/${event.payload.aggregateId}/communication`);
    }
    if (event.payload.resourceType === 'SUBMISSION' && COMMUNICATION_PLUGIN_ENABLED) {
      push(`/credit/submissions/${event.payload.aggregateId}/communication`);
    }
  }, []);

  OPEN_EXISTING_DATA_INQUIRY_OVERVIEW.useEvent(event =>
    window
      .open(`/credit/existing-data-inquiries/${event.payload.existingDataInquiryId}`, '_blank')
      .focus(),
  );

  OPEN_EXISTING_DATA_INQUIRY_DOCUMENTS_OVERVIEW.useEvent(event =>
    push(`/credit/existing-data-inquiries/${event.payload.existingDataInquiryId}/documents`),
  );

  useEffect(() => {
    (async () => {
      try {
        const response = await Axios.get(`/authorizations/me`, {});
        if (response?.data?.user_id !== undefined) {
          setProviderId(response.data.provider_organization_id);
          setCurrentUserId(response.data.user_id);
        } else {
          apm.captureError(`user has no user_id. user data: ${JSON.stringify(response.data)}`);
          window.location.replace(`/authorizations/banker/login`);
        }
      } catch (error) {
        apm.captureError(error);
      }
    })();
  }, [setProviderId, setCurrentUserId]);

  useFrontendNotifications();

  return (
    <div className={styles.viewport}>
      <Toaster />
      <div className={styles.content}>
        <Switch>
          <Redirect from="/" to="/dashboard/" exact />
          <ApmRoute path="/dashboard/" exact>
            <DashboardWrapper currentUserId={currentUserId} providerId={providerId} />
          </ApmRoute>
          <ApmRoute path="/archive">
            <ArchiveWrapper currentUserId={currentUserId} providerId={providerId} />
          </ApmRoute>
          <ApmRoute path="/credit/submissions/:submissionId" exact>
            <SubmissionOverviewWrapper currentUserId={currentUserId} providerId={providerId} />
          </ApmRoute>
          <ApmRoute path="/credit/submissions/:submissionId/documents">
            <SubmissionDocumentsWrapper providerId={providerId} />
          </ApmRoute>
          <ApmRoute path="/credit/inquiries/:inquiryId" exact>
            <InquiryOverviewWrapper currentUserId={currentUserId} providerId={providerId} />
          </ApmRoute>
          <ApmRoute path="/credit/inquiries/:inquiryId/documents">
            <InquiryDocumentsWrapper providerId={providerId} />
          </ApmRoute>
          {COMMUNICATION_PLUGIN_ENABLED && (
            <ApmRoute
              path="/credit/submissions/:submissionId/communication"
              component={() => <SubmissionCommunicationOverviewWrapper />}
            />
          )}
          <ApmRoute
            path="/credit/inquiries/:inquiryId/communication"
            component={() => <InquiryCommunicationOverviewWrapper />}
          />
          <ApmRoute path="/credit/existing-data-inquiries/:existingDataInquiryId" exact>
            <ExistingDataInquiryOverviewWrapper
              currentUserId={currentUserId}
              providerId={providerId}
            />
          </ApmRoute>
          <ApmRoute path="/credit/existing-data-inquiries/:existingDataInquiryId/documents">
            <ExistingDataInquiryDocumentsWrapper providerId={providerId} />
          </ApmRoute>
        </Switch>
      </div>
      <Footer
        copyrightInfo={{ companyName: 'Interhyp AG' }}
        configurableLinks={[
          { label: 'Datenschutz', href: 'https://interhyp.de/datenschutz' },
          { label: 'Impressum', href: 'https://interhyp.de/impressum' },
        ]}
      />
    </div>
  );
}
